import React, { useState, useEffect } from 'react';
import { 
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Divider,
  useToast,
  Spinner,
  Icon,
  Input,
} from '@chakra-ui/react';
import { 
  FiCreditCard,
  FiSettings,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  formatUsdFromCents,
  formatUsdFromDollars,
  parseUsdInputToDollars,
} from '../utils/formatCurrency';
import {
  fetchEmailInfraBillingAssets,
  cancelMailbox,
  revertMailboxCancel,
  cancelDomain,
  revertDomainCancel,
} from '../services/billingEmailInfraService';

const BillingPage = () => {
  const { user, token } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [capInputUsd, setCapInputUsd] = useState('');
  const [capSaving, setCapSaving] = useState(false);
  const [emailInfra, setEmailInfra] = useState(null);
  const [emailInfraLoading, setEmailInfraLoading] = useState(false);
  const [emailInfraActionId, setEmailInfraActionId] = useState(null);

  useEffect(() => {
    const cap = subscriptionStatus?.credit_overage_spend_cap_cents;
    if (cap != null && cap > 0) {
      setCapInputUsd(formatUsdFromCents(cap));
    } else {
      setCapInputUsd('');
    }
  }, [subscriptionStatus?.credit_overage_spend_cap_cents]);

  useEffect(() => {
    if (user && token) {
      fetchBillingData();
    }
  }, [user, token]);

  const fetchEmailInfra = async () => {
    setEmailInfraLoading(true);
    try {
      localStorage.setItem('token', token);
      const data = await fetchEmailInfraBillingAssets();
      setEmailInfra(data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching email infra billing:', error);
      }
      setEmailInfra(null);
    } finally {
      setEmailInfraLoading(false);
    }
  };

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      localStorage.setItem('token', token);
      const subscriptionResponse = await api.get('/api/payments/subscription-status');
      setSubscriptionStatus(subscriptionResponse.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load billing information. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
    const tier = user?.subscription || 'free';
    if (tier !== 'free') {
      fetchEmailInfra();
    }
  };

  const formatEffectiveDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const handleCancelMailbox = async (mb) => {
    const when = formatEffectiveDate(mb.cancel_effective_at || mb.billing_period_end);
    const ok = window.confirm(
      `Cancel inbox ${mb.email || mb.id}? It stays active until ${when || 'the end of your billing period'}, then the provisioned inbox is removed, email warming stops and the warming seat is released, and sequencer campaigns for this inbox are deleted (sent/received messages stay in your inbox).`
    );
    if (!ok) return;
    setEmailInfraActionId(mb.id);
    try {
      localStorage.setItem('token', token);
      const res = await cancelMailbox(mb.id);
      toast({
        title: 'Inbox cancellation scheduled',
        description: res.message,
        status: 'info',
        duration: 6000,
        isClosable: true,
      });
      await fetchEmailInfra();
    } catch (e) {
      toast({
        title: 'Could not cancel inbox',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEmailInfraActionId(null);
    }
  };

  const handleRevertMailbox = async (mb) => {
    setEmailInfraActionId(mb.id);
    try {
      localStorage.setItem('token', token);
      await revertMailboxCancel(mb.id);
      toast({ title: 'Inbox cancellation removed', status: 'success', duration: 3000, isClosable: true });
      await fetchEmailInfra();
    } catch (e) {
      toast({
        title: 'Could not undo cancellation',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEmailInfraActionId(null);
    }
  };

  const handleCancelDomain = async (dom) => {
    const ok = window.confirm(
      `Cancel domain ${dom.fqdn}? Auto-renew turns off; the domain stays live until its annual renewal. Then all inboxes on it are removed and billing and warming for those inboxes end.`
    );
    if (!ok) return;
    setEmailInfraActionId(dom.id);
    try {
      localStorage.setItem('token', token);
      const res = await cancelDomain(dom.id);
      toast({
        title: 'Domain cancellation scheduled',
        description: res.message,
        status: 'info',
        duration: 6000,
        isClosable: true,
      });
      await fetchEmailInfra();
    } catch (e) {
      toast({
        title: 'Could not cancel domain',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEmailInfraActionId(null);
    }
  };

  const handleRevertDomain = async (dom) => {
    setEmailInfraActionId(dom.id);
    try {
      localStorage.setItem('token', token);
      await revertDomainCancel(dom.id);
      toast({ title: 'Domain cancellation removed', status: 'success', duration: 3000, isClosable: true });
      await fetchEmailInfra();
    } catch (e) {
      toast({
        title: 'Could not undo domain cancellation',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEmailInfraActionId(null);
    }
  };

  const handleSaveOverageCap = async () => {
    const dollars = parseUsdInputToDollars(capInputUsd);
    if (Number.isNaN(dollars) || dollars < 0) {
      toast({
        title: 'Enter a valid dollar amount (0 or greater)',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setCapSaving(true);
    try {
      localStorage.setItem('token', token);
      await api.patch('/api/payments/credit-overage-cap', {
        credit_overage_spend_cap_cents: Math.round(dollars * 100),
      });
      toast({ title: 'Spend cap saved', status: 'success', duration: 3000, isClosable: true });
      await fetchBillingData();
    } catch (e) {
      toast({
        title: 'Could not save cap',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCapSaving(false);
    }
  };

  const handleSubscribe = async (productKey, isResume = false) => {
    setActionLoading(true);
    try {
      localStorage.setItem('token', token);
      
      const isNewSubscription = currentTier === 'free';
      
      if (isNewSubscription) {
        const response = await api.post('/api/payments/create-subscription-session', {
          product_key: productKey,
          success_url: `${window.location.origin}/billing?success=true`,
          cancel_url: `${window.location.origin}/billing?canceled=true`
        });
        const { url } = response.data;
        window.location.href = url;
      } else {
        const response = await api.post('/api/payments/change-subscription', {
          product_key: productKey
        });
        
        const { status, change_type, message } = response.data;
        
        if (status === 'already_subscribed') {
          toast({
            title: 'Already Subscribed',
            description: message,
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        } else if (status === 'success') {
          let titleText, descText;
          
          if (change_type === 'cancel_scheduled_change') {
            titleText = 'Change Cancelled';
            descText = 'Your scheduled subscription change has been cancelled. You will remain on your current plan.';
          } else if (isResume || change_type === 'resume') {
            titleText = 'Subscription Resumed!';
            descText = 'Your subscription has been resumed successfully.';
          } else if (change_type === 'upgrade') {
            titleText = 'Upgraded Successfully!';
            descText = 'Your subscription has been upgraded and prorated. You\'ll be charged for the remaining time at the new rate.';
          } else if (change_type === 'downgrade') {
            titleText = 'Downgrade Scheduled';
            descText = message || 'Your subscription will be downgraded at the end of your current billing period.';
          } else {
            titleText = 'Success';
            descText = message || 'Your subscription has been updated.';
          }
          
          toast({
            title: titleText,
            description: descText,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          
          if (change_type === 'upgrade' || change_type === 'resume' || isResume) {
            window.dispatchEvent(new Event('subscriptionUpgraded'));
          } else if (change_type === 'cancel_scheduled_change') {
            window.dispatchEvent(new Event('subscriptionChanged'));
          } else {
            window.dispatchEvent(new Event('subscriptionDowngraded'));
          }
          
          setTimeout(() => fetchBillingData(), 1000);
        }
      }
    } catch (error) {
      console.error('Subscription failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to process subscription. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      localStorage.setItem('token', token);
      const response = await api.post('/api/payments/create-portal-session', {
        return_url: window.location.href
      });
      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      toast({
        title: 'Error',
        description: 'Failed to open billing portal. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setActionLoading(false);
    }
  };

  const handleCancelScheduledChange = async () => {
    await handleSubscribe(`${currentTier}_subscription`, false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast({
        title: 'Thank you for your purchase!',
        description: 'Your payment has been processed successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => fetchBillingData(), 1000);
    } else if (urlParams.get('canceled') === 'true') {
      toast({
        title: 'Payment Canceled',
        description: 'You can try again anytime.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  if (loading) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a">
        <Spinner size="xl" color="gray.400" />
      </Flex>
    );
  }

  if (!user) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a">
        <Text color="#a3a3a3">Please log in to view your billing information.</Text>
      </Flex>
    );
  }

  const currentTier = user?.subscription || 'free';

  const tierLabels = {
    'free': 'Free',
    'starter': 'Starter',
    'basic': 'Basic',
    'pro': 'Pro',
  };

  const primaryButton = {
    bg: "#fafafa",
    color: "#0a0a0a",
    borderRadius: "full",
    fontWeight: "600",
    fontSize: "13px",
    h: "36px",
    _hover: { opacity: 0.85 },
  };

  const outlineButton = {
    bg: "transparent",
    borderRadius: "full",
    fontWeight: "500",
    fontSize: "13px",
    h: "36px",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "rgba(255,255,255,0.6)",
    _hover: { borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.9)", bg: "rgba(255,255,255,0.04)" },
  };

  const dangerButton = {
    bg: "transparent",
    borderRadius: "full",
    fontWeight: "500",
    fontSize: "13px",
    h: "36px",
    border: "1px solid rgba(239,68,68,0.3)",
    color: "rgba(239,68,68,0.7)",
    _hover: { borderColor: "rgba(239,68,68,0.5)", color: "rgba(239,68,68,0.9)", bg: "rgba(239,68,68,0.06)" },
  };

  const cardBox = {
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.10)",
    bg: "#111111",
  };

  const tierBox = {
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.08)",
    bg: "rgba(255,255,255,0.02)",
    p: 5,
  };

  return (
    <Box minH="100vh" bg="#0a0a0a" px={6} pt={10} pb={16}>
      <VStack align="stretch" spacing={8} maxW="700px" mx="auto">
        {/* Header */}
        <Box>
          <Heading
            size="lg"
            color="#fafafa"
            fontWeight="700"
            letterSpacing="-0.02em"
            mb={1}
          >
            Billing
          </Heading>
          <Text fontSize="sm" color="rgba(255,255,255,0.35)" letterSpacing="0.01em">
            Manage your subscription and payment information.
          </Text>
        </Box>

        {/* Current Plan */}
        <Box {...cardBox} p={5}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <HStack spacing={3}>
                <Text fontSize="md" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                  Current Plan
                </Text>
                <Box
                  px={2}
                  py="2px"
                  borderRadius="full"
                  bg="rgba(255,255,255,0.06)"
                  border="1px solid rgba(255,255,255,0.12)"
                >
                  <Text fontSize="11px" fontWeight="500" color="rgba(255,255,255,0.55)" letterSpacing="0.06em" textTransform="uppercase">
                    {tierLabels[currentTier] || 'Free'}
                  </Text>
                </Box>
              </HStack>
            </HStack>

            <Divider borderColor="rgba(255,255,255,0.08)" />

            {currentTier !== 'free' ? (
              <VStack spacing={3} align="stretch">
                {subscriptionStatus?.new_subscription === 'free' ? (
                  <Box
                    p={4}
                    borderRadius="8px"
                    bg="rgba(239,68,68,0.05)"
                    border="1px solid rgba(239,68,68,0.2)"
                  >
                    <VStack spacing={1.5} align="start">
                      <Text color="rgba(239,68,68,0.85)" fontSize="sm" fontWeight="600">
                        Subscription Cancelled
                      </Text>
                      <Text color="#a3a3a3" fontSize="sm" lineHeight="1.7">
                        Your subscription has been cancelled and will end on{' '}
                        {subscriptionStatus?.next_period
                          ? new Date(subscriptionStatus.next_period).toLocaleDateString()
                          : 'the end of your billing period'}.{' '}
                        You can resume it at any time below.
                      </Text>
                    </VStack>
                  </Box>
                ) : subscriptionStatus?.new_subscription && subscriptionStatus?.new_subscription !== currentTier ? (
                  <Box
                    p={4}
                    borderRadius="8px"
                    bg="rgba(251,146,60,0.05)"
                    border="1px solid rgba(251,146,60,0.2)"
                  >
                    <VStack spacing={2} align="start">
                      <Text color="rgba(251,146,60,0.85)" fontSize="sm" fontWeight="600">
                        Scheduled Downgrade
                      </Text>
                      <Text color="#a3a3a3" fontSize="sm" lineHeight="1.7">
                        Your plan will change to {tierLabels[subscriptionStatus.new_subscription] || subscriptionStatus.new_subscription} on{' '}
                        {subscriptionStatus?.next_period
                          ? new Date(subscriptionStatus.next_period).toLocaleDateString()
                          : 'your next billing date'}.
                      </Text>
                      <Button
                        {...outlineButton}
                        mt={1}
                        onClick={handleCancelScheduledChange}
                        isLoading={actionLoading}
                        loadingText="Loading..."
                      >
                        Cancel Scheduled Change
                      </Button>
                    </VStack>
                  </Box>
                ) : (
                  <Text color="#a3a3a3" fontSize="sm">
                    You&apos;re on the {tierLabels[currentTier]} plan. Manage your subscription through Stripe.
                  </Text>
                )}
                {subscriptionStatus?.new_subscription !== 'free' && (
                  <Button
                    {...outlineButton}
                    leftIcon={<Icon as={FiSettings} boxSize={3.5} />}
                    alignSelf="start"
                    onClick={handleManageSubscription}
                    isLoading={actionLoading}
                    loadingText="Loading..."
                  >
                    Manage Subscription
                  </Button>
                )}
              </VStack>
            ) : (
              <Text color="#a3a3a3" fontSize="sm">
                Upgrade to a paid plan for more monthly credits.
              </Text>
            )}
          </VStack>
        </Box>

        {currentTier !== 'free' && subscriptionStatus && (
          <Box {...cardBox} p={5}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                Credit overage (pay as you go)
              </Text>
              <Text fontSize="sm" color="#a3a3a3" lineHeight="1.6">
                When your included credits run out, you can keep using the product by setting a monthly spend cap.
                Usage is billed per credit through Stripe at your plan&apos;s overage rate.
              </Text>
              {subscriptionStatus.overage_price_per_credit_cents != null && (
                <Text fontSize="xs" color="rgba(255,255,255,0.45)">
                  Overage rate: {formatUsdFromCents(subscriptionStatus.overage_price_per_credit_cents)} per credit
                </Text>
              )}
              <Text fontSize="sm" color="#a3a3a3">
                This billing period:{' '}
                <Text as="span" color="#fafafa" fontWeight="600">
                  {formatUsdFromCents(subscriptionStatus.credit_overage_spend_cents_this_period || 0)}
                </Text>{' '}
                spent
                {subscriptionStatus.credit_overage_spend_cap_cents != null &&
                subscriptionStatus.credit_overage_spend_cap_cents > 0 ? (
                  <>
                    {' '}
                    of{' '}
                    <Text as="span" color="#fafafa" fontWeight="600">
                      {formatUsdFromCents(subscriptionStatus.credit_overage_spend_cap_cents)}
                    </Text>{' '}
                    cap
                  </>
                ) : (
                  <Text as="span" color="rgba(255,255,255,0.35)"> — cap not set (overage off)</Text>
                )}
              </Text>
              <HStack spacing={2} align="flex-end" flexWrap="wrap">
                <Box flex="1" minW="120px">
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={1}>
                    Monthly cap (USD)
                  </Text>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={capInputUsd}
                    onChange={(e) => setCapInputUsd(e.target.value)}
                    onBlur={() => {
                      const d = parseUsdInputToDollars(capInputUsd);
                      if (!Number.isNaN(d) && String(capInputUsd).trim() !== '') {
                        setCapInputUsd(formatUsdFromDollars(d));
                      }
                    }}
                    placeholder="0 = disable overage"
                    bg="rgba(255,255,255,0.04)"
                    borderColor="rgba(255,255,255,0.12)"
                    color="#fafafa"
                    size="sm"
                  />
                </Box>
                <Button
                  {...primaryButton}
                  w="auto"
                  px={6}
                  onClick={handleSaveOverageCap}
                  isLoading={capSaving}
                  loadingText="Saving"
                >
                  Save cap
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {currentTier !== 'free' && (
          <Box {...cardBox} p={5}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                Email domains &amp; inboxes
              </Text>
              <Text fontSize="sm" color="#a3a3a3" lineHeight="1.6">
                Cancel an inbox to stop mailbox and included email warming billing at the end of your
                current period; the provisioned inbox is removed on the provider&apos;s schedule and
                sequencer campaigns for that inbox are removed when your billing period ends (inbox
                history is kept). Cancel a domain to turn off auto-renew; at annual renewal, all
                inboxes on that domain are cancelled the same way.
              </Text>
              {emailInfraLoading ? (
                <HStack justify="center" py={6}>
                  <Spinner size="md" color="gray.400" />
                </HStack>
              ) : !emailInfra?.mailboxes?.length && !emailInfra?.domains?.length ? (
                <Text fontSize="sm" color="rgba(255,255,255,0.35)">
                  No provisioned domains or inboxes yet.
                </Text>
              ) : (
                <VStack spacing={4} align="stretch">
                  {emailInfra?.mailboxes?.length > 0 && (
                    <Box>
                      <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={2} textTransform="uppercase" letterSpacing="0.06em">
                        Inboxes
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {emailInfra.mailboxes.map((mb) => {
                          const scheduled = mb.cancellation_status === 'scheduled';
                          const effective = formatEffectiveDate(
                            mb.cancel_effective_at || mb.billing_period_end
                          );
                          return (
                            <HStack
                              key={mb.id}
                              justify="space-between"
                              align="flex-start"
                              p={3}
                              borderRadius="8px"
                              border="1px solid rgba(255,255,255,0.08)"
                              bg="rgba(255,255,255,0.02)"
                              flexWrap="wrap"
                              gap={2}
                            >
                              <Box flex="1" minW="160px">
                                <Text fontSize="sm" color="#fafafa" fontWeight="500">
                                  {mb.email || mb.fqdn || 'Inbox'}
                                </Text>
                                <Text fontSize="xs" color="rgba(255,255,255,0.4)">
                                  {mb.provider === 'outlook' ? 'Outlook' : 'Gmail'}
                                  {mb.fqdn ? ` · ${mb.fqdn}` : ''}
                                </Text>
                                {scheduled && (
                                  <Text fontSize="xs" color="rgba(251,146,60,0.85)" mt={1}>
                                    Cancels {effective ? `on ${effective}` : 'at period end'}
                                  </Text>
                                )}
                              </Box>
                              {scheduled ? (
                                <Button
                                  {...outlineButton}
                                  size="sm"
                                  onClick={() => handleRevertMailbox(mb)}
                                  isLoading={emailInfraActionId === mb.id}
                                >
                                  Keep inbox
                                </Button>
                              ) : (
                                <Button
                                  {...dangerButton}
                                  size="sm"
                                  onClick={() => handleCancelMailbox(mb)}
                                  isLoading={emailInfraActionId === mb.id}
                                >
                                  Cancel inbox
                                </Button>
                              )}
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}
                  {emailInfra?.domains?.length > 0 && (
                    <Box>
                      <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={2} textTransform="uppercase" letterSpacing="0.06em">
                        Domains
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {emailInfra.domains.map((dom) => {
                          const scheduled = dom.domain_cancellation_status === 'scheduled';
                          const expires = formatEffectiveDate(dom.expires_at);
                          return (
                            <HStack
                              key={dom.id}
                              justify="space-between"
                              align="flex-start"
                              p={3}
                              borderRadius="8px"
                              border="1px solid rgba(255,255,255,0.08)"
                              bg="rgba(255,255,255,0.02)"
                              flexWrap="wrap"
                              gap={2}
                            >
                              <Box flex="1" minW="160px">
                                <Text fontSize="sm" color="#fafafa" fontWeight="500">
                                  {dom.fqdn}
                                </Text>
                                <Text fontSize="xs" color="rgba(255,255,255,0.4)">
                                  {dom.provider === 'outlook' ? 'Outlook' : 'Gmail'} workspace
                                  {dom.auto_renew === false ? ' · auto-renew off' : ''}
                                </Text>
                                {scheduled && (
                                  <Text fontSize="xs" color="rgba(251,146,60,0.85)" mt={1}>
                                    {expires
                                      ? `Active until ${expires}; inboxes cancel then`
                                      : 'Active until renewal; inboxes cancel then'}
                                  </Text>
                                )}
                              </Box>
                              {scheduled ? (
                                <Button
                                  {...outlineButton}
                                  size="sm"
                                  onClick={() => handleRevertDomain(dom)}
                                  isLoading={emailInfraActionId === dom.id}
                                >
                                  Keep domain
                                </Button>
                              ) : (
                                <Button
                                  {...dangerButton}
                                  size="sm"
                                  onClick={() => handleCancelDomain(dom)}
                                  isLoading={emailInfraActionId === dom.id}
                                >
                                  Cancel domain
                                </Button>
                              )}
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              )}
            </VStack>
          </Box>
        )}

        {/* Plans */}
        <Box {...cardBox} p={5}>
          <VStack spacing={5} align="stretch">
            <HStack spacing={3}>
              <Text fontSize="md" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                {currentTier === 'free' ? 'Subscription Plans' : 'Upgrade Options'}
              </Text>
            </HStack>

            <Divider borderColor="rgba(255,255,255,0.08)" />

            {/* Credit costs info */}
            <Box
              p={4}
              borderRadius="8px"
              border="1px solid rgba(255,255,255,0.08)"
              bg="rgba(255,255,255,0.02)"
            >
              <Text fontSize="sm" fontWeight="500" color="#fafafa" mb={3}>Credit Costs</Text>
              <VStack spacing={2} align="stretch">
                {[
                  { label: 'Contact enrichment', cost: '1 credit / contact' },
                  { label: 'Email verification', cost: '1 credit / email' },
                  { label: 'AI email drafts', cost: '1 credit / contact with email' },
                  { label: 'Custom field generation', cost: '1 credit / company' },
                  { label: 'File upload', cost: '100 credits / file' },
                ].map(({ label, cost }) => (
                  <HStack key={label} justify="space-between" align="flex-start">
                    <Text fontSize="sm" color="#a3a3a3">{label}</Text>
                    <Text fontSize="sm" fontWeight="500" color="#fafafa" textAlign="right">{cost}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            <Divider borderColor="rgba(255,255,255,0.08)" />

            {subscriptionStatus?.new_subscription === 'free' && (
              <Text color="#a3a3a3" fontSize="sm">
                Your subscription has been cancelled. Resume your current plan or upgrade below.
              </Text>
            )}
            {subscriptionStatus?.new_subscription !== 'free' && currentTier === 'free' && (
              <Text color="#a3a3a3" fontSize="sm">
                Choose a plan that fits your needs. All plans include monthly credits that renew automatically.
              </Text>
            )}
            {subscriptionStatus?.new_subscription !== 'free' && currentTier !== 'free' && currentTier !== 'pro' && (
              <Text color="#a3a3a3" fontSize="sm">
                Upgrade to a higher tier for more monthly credits.
              </Text>
            )}
            {subscriptionStatus?.new_subscription !== 'free' && currentTier === 'pro' && (
              <Text color="#a3a3a3" fontSize="sm" textAlign="center" py={2}>
                You&apos;re on the highest tier. Enjoy 100,000 credits per month.
              </Text>
            )}

            <VStack spacing={3} align="stretch">
              {/* Starter */}
              {(currentTier === 'free' || currentTier === 'basic' || currentTier === 'pro' || currentTier === 'starter') && (
                <Box {...tierBox}>
                  <VStack align="start" spacing={3}>
                    <HStack justify="space-between" w="100%">
                      <HStack spacing={2}>
                        <Text fontWeight="600" color="#fafafa" fontSize="sm">Starter</Text>
                        {subscriptionStatus?.new_subscription === 'starter' && currentTier !== 'starter' && (
                          <Box px={1.5} py="1px" borderRadius="full" bg="rgba(251,146,60,0.12)" border="1px solid rgba(251,146,60,0.3)">
                            <Text fontSize="9px" color="rgba(251,146,60,0.9)" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">Scheduled</Text>
                          </Box>
                        )}
                        {currentTier === 'starter' && subscriptionStatus?.new_subscription !== 'free' && (
                          <Box px={1.5} py="1px" borderRadius="full" bg="rgba(34,197,94,0.1)" border="1px solid rgba(34,197,94,0.3)">
                            <Text fontSize="9px" color="#4ade80" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">Current</Text>
                          </Box>
                        )}
                      </HStack>
                      <Text fontWeight="600" color="#fafafa" fontSize="sm">$200 / month</Text>
                    </HStack>
                    <Text fontSize="xs" color="#a3a3a3">3,500 credits per month</Text>
                    {currentTier === 'starter' ? (
                      subscriptionStatus?.new_subscription === 'free' ? (
                        <Button {...primaryButton} leftIcon={<Icon as={FiSettings} boxSize={3.5} />} onClick={handleManageSubscription} isLoading={actionLoading} loadingText="Loading...">
                          Resume Subscription
                        </Button>
                      ) : null
                    ) : subscriptionStatus?.new_subscription === 'starter' && currentTier !== 'starter' ? (
                      <Button {...dangerButton} leftIcon={<Icon as={FiCreditCard} boxSize={3.5} />} onClick={handleCancelScheduledChange} isLoading={actionLoading} loadingText="Loading...">
                        Cancel Downgrade
                      </Button>
                    ) : (
                      <Button {...primaryButton} leftIcon={<Icon as={FiCreditCard} boxSize={3.5} />} onClick={() => handleSubscribe('starter_subscription')} isLoading={actionLoading} loadingText="Loading...">
                        {currentTier === 'free' ? 'Subscribe to Starter' : 'Downgrade to Starter'}
                      </Button>
                    )}
                  </VStack>
                </Box>
              )}

              {/* Basic */}
              <Box {...tierBox}>
                <VStack align="start" spacing={3}>
                  <HStack justify="space-between" w="100%">
                    <HStack spacing={2}>
                      <Text fontWeight="600" color="#fafafa" fontSize="sm">Basic</Text>
                      {subscriptionStatus?.new_subscription === 'basic' && currentTier !== 'basic' && (
                        <Box px={1.5} py="1px" borderRadius="full" bg="rgba(251,146,60,0.12)" border="1px solid rgba(251,146,60,0.3)">
                          <Text fontSize="9px" color="rgba(251,146,60,0.9)" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">Scheduled</Text>
                        </Box>
                      )}
                      {currentTier === 'basic' && subscriptionStatus?.new_subscription !== 'free' && (
                        <Box px={1.5} py="1px" borderRadius="full" bg="rgba(34,197,94,0.1)" border="1px solid rgba(34,197,94,0.3)">
                          <Text fontSize="9px" color="#4ade80" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">Current</Text>
                        </Box>
                      )}
                    </HStack>
                    <Text fontWeight="600" color="#fafafa" fontSize="sm">$800 / month</Text>
                  </HStack>
                  <Text fontSize="xs" color="#a3a3a3">20,000 credits per month</Text>
                  {currentTier === 'basic' ? (
                    subscriptionStatus?.new_subscription === 'free' ? (
                      <Button {...primaryButton} leftIcon={<Icon as={FiSettings} boxSize={3.5} />} onClick={handleManageSubscription} isLoading={actionLoading} loadingText="Loading...">
                        Resume Subscription
                      </Button>
                    ) : null
                  ) : subscriptionStatus?.new_subscription === 'basic' && currentTier !== 'basic' ? (
                    <Button {...dangerButton} leftIcon={<Icon as={FiCreditCard} boxSize={3.5} />} onClick={handleCancelScheduledChange} isLoading={actionLoading} loadingText="Loading...">
                      Cancel Downgrade
                    </Button>
                  ) : (
                    <Button {...primaryButton} leftIcon={<Icon as={FiCreditCard} boxSize={3.5} />} onClick={() => handleSubscribe('basic_subscription')} isLoading={actionLoading} loadingText="Loading...">
                      {currentTier === 'free' ? 'Subscribe to Basic' : currentTier === 'starter' ? 'Upgrade to Basic' : 'Downgrade to Basic'}
                    </Button>
                  )}
                </VStack>
              </Box>

              {/* Pro */}
              <Box {...tierBox}>
                <VStack align="start" spacing={3}>
                  <HStack justify="space-between" w="100%">
                    <HStack spacing={2}>
                      <Text fontWeight="600" color="#fafafa" fontSize="sm">Pro</Text>
                      {subscriptionStatus?.new_subscription === 'pro' && currentTier !== 'pro' && (
                        <Box px={1.5} py="1px" borderRadius="full" bg="rgba(251,146,60,0.12)" border="1px solid rgba(251,146,60,0.3)">
                          <Text fontSize="9px" color="rgba(251,146,60,0.9)" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">Scheduled</Text>
                        </Box>
                      )}
                      {currentTier === 'pro' && subscriptionStatus?.new_subscription !== 'free' && (
                        <Box px={1.5} py="1px" borderRadius="full" bg="rgba(34,197,94,0.1)" border="1px solid rgba(34,197,94,0.3)">
                          <Text fontSize="9px" color="#4ade80" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">Current</Text>
                        </Box>
                      )}
                    </HStack>
                    <Text fontWeight="600" color="#fafafa" fontSize="sm">$3,000 / month</Text>
                  </HStack>
                  <Text fontSize="xs" color="#a3a3a3">100,000 credits per month</Text>
                  {currentTier === 'pro' ? (
                    subscriptionStatus?.new_subscription === 'free' ? (
                      <Button {...primaryButton} leftIcon={<Icon as={FiSettings} boxSize={3.5} />} onClick={handleManageSubscription} isLoading={actionLoading} loadingText="Loading...">
                        Resume Subscription
                      </Button>
                    ) : null
                  ) : subscriptionStatus?.new_subscription === 'pro' && currentTier !== 'pro' ? (
                    <Button {...dangerButton} leftIcon={<Icon as={FiCreditCard} boxSize={3.5} />} onClick={handleCancelScheduledChange} isLoading={actionLoading} loadingText="Loading...">
                      Cancel Downgrade
                    </Button>
                  ) : (
                    <Button {...primaryButton} leftIcon={<Icon as={FiCreditCard} boxSize={3.5} />} onClick={() => handleSubscribe('pro_subscription')} isLoading={actionLoading} loadingText="Loading...">
                      {currentTier === 'free' ? 'Subscribe to Pro' : 'Upgrade to Pro'}
                    </Button>
                  )}
                </VStack>
              </Box>
            </VStack>

            <Text color="rgba(255,255,255,0.2)" fontSize="xs" textAlign="center">
              Secure payment powered by Stripe. Cancel anytime.
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default BillingPage;
